import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import LoadingButton from "../../../../components/LoadingButton/LoadingButton";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { useTranslation } from "react-i18next";
import { gsap } from "gsap";

const LOCALE = "modules_Guests_pages_GuestPaymentPage_MobileSummary";

interface MobileSummaryProps {
  totalPrice: number;
  serviceCost: number;
  serviceTax: number;
  finalTotal: number;
  progress: number;
  loading: boolean;
  handlePayment: () => Promise<void>;
  disablePayment: boolean;
}

const MobileSummary: React.FC<MobileSummaryProps> = ({
  totalPrice,
  serviceCost,
  serviceTax,
  finalTotal,
  progress,
  loading,
  handlePayment,
  disablePayment,
}) => {
  const { t } = useTranslation(LOCALE);
  const [isExpanded, setIsExpanded] = useState(false);
  const summaryContentId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const summaryWrapperRef = useRef<HTMLDivElement>(null);
  const summaryInnerRef = useRef<HTMLDivElement>(null);

  const toggleLabel = isExpanded
    ? t("collapse_purchase_summary", {
        defaultValue: "Ocultar desglose de compra",
      })
    : t("expand_purchase_summary", {
        defaultValue: "Mostrar desglose de compra",
      });

  useLayoutEffect(() => {
    if (!containerRef.current || !summaryWrapperRef.current) {
      return;
    }

    gsap.set(containerRef.current, { paddingTop: 24 });
    gsap.set(summaryWrapperRef.current, {
      height: 0,
      autoAlpha: 0,
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const wrapper = summaryWrapperRef.current;
    const inner = summaryInnerRef.current;

    if (!container || !wrapper || !inner) {
      return;
    }

    const contentHeight = inner.scrollHeight;
    const tl = gsap.timeline({
      defaults: { duration: 0.35, ease: "power2.out" },
    });

    if (isExpanded) {
      tl.to(
        wrapper,
        {
          height: contentHeight,
          autoAlpha: 1,
          onComplete: () => {
            gsap.set(wrapper, { height: "auto" });
          },
        },
        0
      ).to(
        container,
        {
          paddingTop: 32,
        },
        0
      );
    } else {
      tl.to(
        wrapper,
        {
          height: 0,
          autoAlpha: 0,
          onComplete: () => {
            gsap.set(wrapper, { height: 0 });
          },
        },
        0
      ).to(
        container,
        {
          paddingTop: 24,
        },
        0
      );
    }

    return () => {
      tl.kill();
    };
  }, [isExpanded]);

  return (
    <div
      ref={containerRef}
      className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-primary px-4 pb-4 text-white"
    >
      <button
        type="button"
        className="absolute left-1/2 top-2 flex h-8 w-16 -translate-x-1/2 items-center justify-center rounded-full focus:outline-none focus-visible:outline-none"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        aria-controls={summaryContentId}
        aria-label={toggleLabel}
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <span className="block h-1.5 w-10 rounded-full bg-white/80" />
      </button>

      <div
        ref={summaryWrapperRef}
        id={summaryContentId}
        aria-hidden={!isExpanded}
        className="overflow-hidden"
      >
        <div ref={summaryInnerRef}>
          <h2 className="mb-2 text-lg font-semibold">{t("purchase_summary")}</h2>
          <div className="space-y-2 border-b border-white/30 pb-3 text-sm text-white/90">
            <div className="flex justify-between">
              <span>{t("subtotal")}</span>
              <span>
                {totalPrice.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("service_cost")}</span>
              <span>
                {serviceCost.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("tax", { defaultValue: "IVA" })}</span>
              <span>
                {serviceTax.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`flex items-center justify-between text-lg font-bold ${
          isExpanded ? "mt-4" : "mt-2"
        } mb-3`}
      >
        <span>{t("total")}</span>
        <span>
          {finalTotal.toLocaleString("es-MX", {
            style: "currency",
            currency: "MXN",
          })}
        </span>
      </div>
      <LoadingButton
        label={t("proceed_to_payment")}
        onClick={handlePayment}
        progress={progress}
        type="primary"
        className="mt-1 w-full bg-white text-primary"
        disabled={loading || disablePayment}
      />
    </div>
  );
};

export default withTranslations(LOCALE)(MobileSummary);
